/**
===================================================
Module: frontend.src.components.EditProjectModal.tsx
Description: Modal component for editing project details
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/
import React, { useState, useEffect } from "react";
import "../styles/EditProjectModal.css";

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, description: string) => Promise<void>;
  projectName: string;
  projectDescription: string;
  isUpdating: boolean;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  projectName,
  projectDescription,
  isUpdating,
}) => {
  const [name, setName] = useState(projectName);
  const [description, setDescription] = useState(projectDescription);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setName(projectName);
      setDescription(projectDescription);
      setError("");
    }
  }, [isOpen, projectName, projectDescription]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError("Project name is required");
      return;
    }

    try {
      await onSubmit(name.trim(), description.trim());
      onClose();
    } catch (error) {
      setError("Failed to update project. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edit-project-modal-overlay" onClick={onClose}>
      <div
        className="edit-project-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="edit-project-modal-header">
          <h3 className="edit-project-modal-title">フォルダの編集</h3>
          <button className="edit-project-modal-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-project-modal-body">
          <div className="form-group">
            <label className="form-label">
              フォルダ名 <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="フォルダ名を入力してください"
              maxLength={255}
              disabled={isUpdating}
            />
          </div>

          <div className="form-group">
            <label className="form-label">説明</label>
            <textarea
              className="form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="フォルダの説明を入力してください"
              maxLength={1000}
              disabled={isUpdating}
            />
            <div className="character-count">
              {description.length}/1000 文字
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
        </form>

        <div className="edit-project-modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={isUpdating}
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={isUpdating}
          >
            {isUpdating ? "更新中..." : "更新"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProjectModal;
